import { Avatar, Flex, Menu, MenuButton, MenuItem as GenericMenuItem, MenuList, useDisclosure, Box, Icon } from '@chakra-ui/react';
import styled from '@emotion/styled/macro';
import { CaretDown } from 'components/Icons/Icons';
import { useTranslation } from 'react-i18next';
import { ShepherdTourContext } from 'react-shepherd';
import { ReactComponent as LogOutIcon } from 'assets/svgs/logout.svg';
import { logout } from 'modules/auth/authSlice';
import { currentUserSelector } from 'modules/user/userSlice';
import { isGuestContextSelector } from 'modules/app/appSlice';
import { FC, useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useBreakpoint from 'hooks/useBreakpoint';
import useRestartOnboarding from 'hooks/useRestartOnboarding';
import { rem } from 'utils/fonts';
import ProfileSwitcher from '../ProfileSwitcher/ProfileSwitcher';
import { Button } from 'components';
import { switchProfileTo } from 'modules/user/userSlice';

interface Props {
  profileItems: MenuItem[];
  companyItems: MenuItem[];
}

const StyledMenuButton = styled(MenuButton)`
  font-size: ${rem(16)};
  min-width: 50px;
  background: transparent;
  padding: 5px;
  height: 50px;

  > svg {
    font-size: ${rem(26)};
    margin-left: 10px;
  }
`;

const StyledButton = styled(Button)`
  height: 44px;
  font-size: ${rem(14)};
  width: 100%;
  border-radius: 100px;
  align-items: center;
`;

const AvatarMenu: FC<Props> = ({ profileItems, companyItems }) => {
  const { t } = useTranslation(['common', 'settings']);
  const { isDesktop } = useBreakpoint();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [tourStart, setTourStart] = useState(false);
  const [customIndex, setCustomIndex] = useState<number>();
  const tour = useContext(ShepherdTourContext);
  const currentUser = useSelector(currentUserSelector);
  const dispatch = useDispatch();
  const { restartOnboarding } = useRestartOnboarding();
  const isNotGuest = !useSelector(isGuestContextSelector);

  const handleLogout = useCallback(() => {
    dispatch(logout());
  }, []);

  const onMenuClose = (): void => {
    setTourStart(false);
    onClose();
    setCustomIndex(undefined);
  };

  const finishTour = (): void => {
    setTourStart(false);
    setCustomIndex(undefined);
  };

  useEffect(() => {
    tour?.on('show', (options: unknown) => {
      if (
        // @ts-ignore
        options.step.id === 'personal-settings' ||
        // @ts-ignore
        options.step.id === 'company-settings' ||
        // @ts-ignore
        options.step.id === 'metadata'
      ) {
        onOpen();
        setTourStart(false);

        // @ts-ignore
        setCustomIndex(options.step.id === 'company-settings' || options.step.id === 'metadata' ? 1 : 0);
      } else {
        setTourStart(true);
        onClose();
      }

      // @ts-ignore
      if (['analytics', 'team-members', 'metadata'].includes(options.step.id)) {
        dispatch(switchProfileTo('Company'));
      }
    });

    tour?.on('start', () => {
      !tourStart && setTourStart(true);
    });

    tour?.on('cancel', onMenuClose);
    tour?.on('complete', finishTour);
  }, []);

  useEffect(() => {
    if (tourStart && isOpen) {
      onMenuClose();
    }
  }, [tourStart]);

  return (
    <Flex align="center" position="relative">
      <Menu isOpen={isOpen} closeOnBlur={!tourStart} onClose={onClose} placement="bottom-end" strategy="fixed" autoSelect={false}>
        <StyledMenuButton borderRadius="6px" aria-label={t('common:profileMenu.showMenu')} color="gray.400" onClick={onOpen}>
          <Flex w="100%" h="100%" align="center">
            <Avatar
              name={isNotGuest ? currentUser?.profile?.name ?? '' : undefined}
              src={isNotGuest ? currentUser?.profile?.pictureUrl ?? '' : undefined}
              size="md"
              fontWeight={500}
            />
            <Icon as={CaretDown} boxSize={5} ml="10px" />
          </Flex>
        </StyledMenuButton>
        <MenuList top={62} right={0} pb="25px" minW="315px" borderRadius="base" id="profile-menu" className="profile-menu">
          <ProfileSwitcher profileItems={profileItems} companyItems={companyItems} customIndex={customIndex} onItemClick={onClose} />
          <Box px="25px">
            {isDesktop && (
              <Flex m="-38px 0 40px 0">
                <Button variant="link" onClick={restartOnboarding} color="other.CTA" justifyContent="flex-start">
                  {t('settings:routes.onboarding')}
                </Button>
              </Flex>
            )}
            <StyledButton
              as={GenericMenuItem}
              variant="outline"
              onClick={handleLogout}
              borderColor="greyLinkActive"
              color="greyLinkActive"
              fontWeight="normal"
              rightIcon={<LogOutIcon />}
              layerStyle="logOutButton"
            >
              {t('common:menu.logout')}
            </StyledButton>
          </Box>
        </MenuList>
      </Menu>
    </Flex>
  );
};

export default AvatarMenu;
